import { useEffect, useMemo, useState } from 'react';
import { getActualsRange, getForecastWindow, getPricesRange, getTemperaturesRange } from '../api';
import { todayISO } from '../utils/billingPeriod';
import { classifySeverity, pctOfHigh } from '../utils/thresholds';
import { usePower } from '../state/PowerContext';

// Aggregate hour-level data for the active billing period:
//   - Actuals for elapsed dates (from /forecast_lines merged across sheets)
//   - Forecasts for today + forward, from the latest sheet's /forecast_window
// Computes the period high for the active series and classifies each hour.
export function usePowerData() {
  const { activePeriod, series } = usePower();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hourRecords, setHourRecords] = useState([]);
  const [latestSheet, setLatestSheet] = useState(null);
  const [priceMap, setPriceMap] = useState({}); // `${date}_${hour}` -> $/MWh
  const [tempMap,  setTempMap]  = useState({}); // `${date}_${hour}` -> °F

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const today = todayISO();

        // Pull the whole period's actuals in one request (was 23+ parallel
        // /forecast_lines calls — browser concurrency limits were silently
        // dropping some, producing a truncated `records` set and a wrong
        // period peak).
        const [actualsResult, pricesResult, tempsResult] = await Promise.all([
          getActualsRange(activePeriod.startISO, activePeriod.endISO).catch(() => null),
          getPricesRange(activePeriod.startISO, activePeriod.endISO).catch(() => null),
          getTemperaturesRange(activePeriod.startISO, activePeriod.endISO).catch(() => null),
        ]);
        if (cancelled) return;

        // Determine the latest sheet_date by sampling a few recent candidates.
        // Start from `today+1` and walk back up to 14 days.
        let latest = null;
        let windowResolved = null;
        for (let i = 0; i < 14; i++) {
          const d = new Date(today + 'T00:00:00');
          d.setDate(d.getDate() - i);
          const iso = d.toISOString().slice(0, 10);
          try {
            const w = await getForecastWindow(iso);
            if (w && w.forecasts && Object.keys(w.forecasts).length) {
              latest = iso;
              windowResolved = w;
              break;
            }
          } catch { /* keep walking */ }
        }
        if (cancelled) return;
        setLatestSheet(latest);

        const windowResult = windowResolved;
        const byKey = new Map(); // key = `${date}_${hour}`

        // Past + today actuals — single call
        const actuals = (actualsResult && actualsResult.actuals) || {};
        for (const d of Object.keys(actuals)) {
          for (const hour of Object.keys(actuals[d])) {
            const a = actuals[d][hour] || {};
            const lrz4 = typeof a.LRZ4_actual === 'number' ? a.LRZ4_actual : null;
            const miso = typeof a.MISO_actual === 'number' ? a.MISO_actual : null;
            if (lrz4 == null && miso == null) continue;
            byKey.set(`${d}_${hour}`, {
              date: d, hour: Number(hour), lrz4, miso,
              isForecast: false, source: 'actual',
            });
          }
        }

        // Forward forecasts from latest sheet (don't overwrite actuals)
        if (windowResult && windowResult.forecasts) {
          const sheetDate = windowResult.sheet_date;
          for (const d of Object.keys(windowResult.forecasts)) {
            if (d < activePeriod.startISO || d > activePeriod.endISO) continue;
            const hours = windowResult.forecasts[d] || {};
            for (const hour of Object.keys(hours)) {
              const key = `${d}_${hour}`;
              if (byKey.has(key)) continue;
              const v = hours[hour] || {};
              const lrz4 = typeof v.LRZ4 === 'number' ? v.LRZ4 : null;
              const miso = typeof v.MISO === 'number' ? v.MISO : null;
              if (lrz4 == null && miso == null) continue;
              byKey.set(key, {
                date: d, hour: Number(hour), lrz4, miso,
                isForecast: true, source: `sheet:${sheetDate}`,
              });
            }
          }
        }

        const records = Array.from(byKey.values()).sort((a, b) =>
          a.date === b.date ? a.hour - b.hour : a.date.localeCompare(b.date)
        );

        const pm = {};
        if (pricesResult && pricesResult.prices) {
          for (const d of Object.keys(pricesResult.prices)) {
            for (const h of Object.keys(pricesResult.prices[d])) {
              pm[`${d}_${Number(h)}`] = pricesResult.prices[d][h];
            }
          }
        }

        const tm = {};
        if (tempsResult && tempsResult.temperatures) {
          for (const d of Object.keys(tempsResult.temperatures)) {
            for (const h of Object.keys(tempsResult.temperatures[d])) {
              tm[`${d}_${Number(h)}`] = tempsResult.temperatures[d][h];
            }
          }
        }

        if (cancelled) return;
        setHourRecords(records);
        setPriceMap(pm);
        setTempMap(tm);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activePeriod.startISO, activePeriod.endISO]);

  const seriesKey = series === 'MISO' ? 'miso' : 'lrz4';

  return useMemo(() => {
    const all = hourRecords
      .map(r => ({
        ...r,
        value: r[seriesKey],
        lmp: priceMap[`${r.date}_${r.hour}`] ?? null,
        temperature: tempMap[`${r.date}_${r.hour}`] ?? null,
      }))
      .filter(r => r.value != null);

    const high = all.reduce(
      (max, r) => (r.value > (max?.value ?? -Infinity) ? r : max),
      null
    );

    const today = todayISO();
    const upcoming = all
      .filter(r => r.date > today || (r.date === today && r.isForecast))
      .map(r => ({
        ...r,
        severity: classifySeverity(r.value, high?.value),
        pct: pctOfHigh(r.value, high?.value),
      }));

    const dangerWindows = upcoming.filter(r => r.severity !== 'normal');

    return { loading, error, latestSheet, high, all, upcoming, dangerWindows };
  }, [hourRecords, priceMap, tempMap, seriesKey, loading, error, latestSheet]);
}
